/* ===========================================================
   Hook fino de data-fetching. Nomes de campos compatíveis com
   TanStack Query de propósito — adotar a lib depois é troca de
   import, não refactor (ver docs/decisions.md).
   Cache em módulo: revisitar uma aba renderiza síncrono.
   =========================================================== */
import { useCallback, useEffect, useState } from "react";
import type { ApiResult } from "./types";

const cache = new Map<string, unknown>();

/* SSG/prerender (scripts/prerender.mjs): a página estática injeta o cache já
   resolvido em window.__API_CACHE__, então a primeira render do cliente nasce
   com os dados (hidratação limpa, sem flash). __API_CACHE_DUMP__ é o gancho que
   o prerender usa para extrair o cache depois que os dados carregam no headless.
   Ambos são no-op no runtime normal (sem window ou sem o global injetado). */
if (typeof window !== "undefined") {
  const w = window as unknown as {
    __API_CACHE__?: [string, unknown][];
    __API_CACHE_DUMP__?: () => string;
  };
  if (Array.isArray(w.__API_CACHE__)) {
    for (const [k, v] of w.__API_CACHE__) cache.set(k, v);
  }
  w.__API_CACHE_DUMP__ = () => JSON.stringify([...cache.entries()]);
}

interface QueryState<T> {
  data: T | undefined;
  isLoading: boolean;
  error: Error | null;
}

/** Fetcher vindo do createApiClient: carrega o escopo do tenant junto. */
export interface ApiFetcher<T> {
  (): Promise<ApiResult<T>>;
  scope?: string;
}

export function useApiQuery<T>(key: string, fetcher: ApiFetcher<T>) {
  // A chave real do cache inclui o tenant (equivalente ao ["recurso", storeId]
  // do TanStack Query) — dados de uma loja nunca respondem por outra.
  const cacheKey = fetcher.scope ? `${fetcher.scope}:${key}` : key;

  const [state, setState] = useState<QueryState<T>>(() =>
    cache.has(cacheKey)
      ? { data: cache.get(cacheKey) as T, isLoading: false, error: null }
      : { data: undefined, isLoading: true, error: null },
  );

  const run = useCallback(() => {
    let alive = true;
    fetcher()
      .then((res) => {
        cache.set(cacheKey, res.data);
        if (alive) setState({ data: res.data, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (alive)
          setState({
            data: undefined,
            isLoading: false,
            error: err instanceof Error ? err : new Error(String(err)),
          });
      });
    return () => {
      alive = false;
    };
    // fetcher é estável por endpoint; a identidade da query é a cacheKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  useEffect(() => {
    if (cache.has(cacheKey)) return;
    return run();
  }, [cacheKey, run]);

  const refetch = useCallback(() => {
    cache.delete(cacheKey);
    setState({ data: undefined, isLoading: true, error: null });
    run();
  }, [cacheKey, run]);

  return { ...state, refetch };
}
